import type { TFunction } from 'i18next'
import CollapsibleProducts from './CollapsibleProducts'
import PolePicker from './PolePicker'
import ProductListItems from './ProductListItems'
import ProductSearch from './ProductSearch'
import type { GroupedProducts } from './groupProducts'
import type { SalesPole } from './poles'
import useProductPanel from './useProductPanel'
import { EmptyText, PanelBody, PanelGroup, ProductList, SaleCard, SideTitle } from './styled-components'

interface ProductPanelProps {
  t: TFunction
  products: GroupedProducts
  activeTab: string
  onSelect: (key: string) => void
  searchValue: string
  setSearchValue: (value: string) => void
  formulaSlotRef: (node: HTMLDivElement | null) => void
  poles: SalesPole[]
  pole: SalesPole | null
  onSelectPole: (id: string) => void
  countOf: (pole: SalesPole) => number
}

const ProductPanel = ({
  t,
  products,
  activeTab,
  onSelect,
  searchValue,
  setSearchValue,
  formulaSlotRef,
  poles,
  pole,
  onSelectPole,
  countOf,
}: ProductPanelProps) => {
  const {
    main,
    secondary,
    leads,
    hasPoles,
    isSearching,
    isWaitingForPole,
    hasProducts,
    hasLeads,
    secondaryKey,
    isSecondaryOpen,
    onPanelKeyDown,
    onSearchChange,
  } = useProductPanel({ products, activeTab, searchValue, setSearchValue, poles, pole })

  return (
    <SaleCard aria-labelledby='additional-sales-products-title'>
      <PanelBody onKeyDown={onPanelKeyDown}>
        {hasPoles && <PolePicker t={t} poles={poles} pole={pole} countOf={countOf} onSelect={onSelectPole} />}
        {isWaitingForPole ? (
          <EmptyText>{t('additionalSales:pulse.pole_required')}</EmptyText>
        ) : (
          <>
            <PanelGroup>
              {!hasPoles && <ProductSearch t={t} value={searchValue} onChange={onSearchChange} />}
              <SideTitle id='additional-sales-products-title'>{t('additionalSales:pulse.product')}</SideTitle>
              <EmptyText hidden={hasProducts}>{t('additionalSales:pulse.no_product')}</EmptyText>
              <ProductList>
                <ProductListItems
                  saleKeys={main}
                  activeTab={activeTab}
                  onSelect={onSelect}
                  formulaSlotRef={formulaSlotRef}
                  t={t}
                />
              </ProductList>
            </PanelGroup>
            <CollapsibleProducts
              key={secondaryKey}
              id='additional-sales-secondary-products'
              label={t('additionalSales:pulse.secondary_products')}
              count={secondary.length}
              initiallyOpen={isSecondaryOpen}
              forceOpen={isSearching}
            >
              <ProductListItems
                saleKeys={secondary}
                activeTab={activeTab}
                onSelect={onSelect}
                formulaSlotRef={formulaSlotRef}
                t={t}
              />
            </CollapsibleProducts>
            {hasLeads && (
              <PanelGroup>
                <SideTitle>{t('additionalSales:pulse.leads')}</SideTitle>
                <ProductList>
                  <ProductListItems
                    saleKeys={leads}
                    activeTab={activeTab}
                    onSelect={onSelect}
                    formulaSlotRef={formulaSlotRef}
                    t={t}
                  />
                </ProductList>
              </PanelGroup>
            )}
          </>
        )}
      </PanelBody>
    </SaleCard>
  )
}

export default ProductPanel
